import { Flame } from "lucide-react";

/**
 * Spinner — Calofay-branded loading indicator.
 *
 * Design:
 *   - Brand-orange Flame icon in the centre (nutrition / streak cue — matches
 *     the icon used throughout the app for calories & streaks).
 *   - Rotating orange gradient arc around it (the actual "loading" animation).
 *   - Subtle scale-pulse on the flame so the composition feels alive even if
 *     the arc rotation is missed (e.g. reduced-motion users still see life).
 *
 * Props:
 *   size: "sm" (chip) | "md" (default) | "lg" (page-level).
 *   bare: when true, renders ONLY the flame+arc visual with no flex padding
 *         wrapper — use this when the caller already supplies its own layout
 *         (e.g. a `min-h-screen flex items-center justify-center` parent, or
 *         when centring the spinner inside a card with `mx-auto`).
 *   label: optional caption text (hidden when `bare`).
 */
export default function Spinner({ size = "md", bare = false, className = "", label }) {
  const dims = size === "sm"
    ? { box: 20, icon: 10, ring: 2, pad: "py-2" }
    : size === "lg"
      // Large spinner = the page-level loader. Fill ~70vh so it visually
      // sits in the vertical center of the viewport instead of hugging
      // the top of the content area. Callers that already wrap it in a
      // `min-h-screen flex items-center` layout should use `bare`.
      ? { box: 56, icon: 28, ring: 4, pad: "min-h-[70vh] py-12" }
      : { box: 36, icon: 18, ring: 3, pad: "py-8" };

  const visual = (
    <div
      className="relative flex items-center justify-center"
      style={{ width: dims.box, height: dims.box }}
    >
      {/* Rotating gradient arc — the primary "loading" motion. SVG so the
          partial-ring gradient stroke renders correctly in Safari / iOS
          WebView. `animate-spin` + `[animation-duration:1.1s]` gives a
          slightly slower, calmer feel than the default 1s. */}
      <svg
        className="absolute inset-0 animate-spin [animation-duration:1.1s]"
        viewBox="0 0 44 44"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="cf-spin-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#FB923C" stopOpacity="0" />
          </linearGradient>
        </defs>
        <circle cx="22" cy="22" r="18" stroke="#FED7AA" strokeOpacity="0.35" strokeWidth={dims.ring} />
        <circle
          cx="22" cy="22" r="18"
          stroke="url(#cf-spin-grad)"
          strokeWidth={dims.ring}
          strokeLinecap="round"
          strokeDasharray="60 200"
        />
      </svg>
      {/* Centre flame — brand-orange, soft fill + subtle scale-pulse.
          `animate-pulse` on the flame itself (not the wrapper) so only
          the icon alpha pulses while the surrounding arc keeps a
          constant stroke. */}
      <Flame
        className="text-brand-500 relative z-10 animate-pulse [animation-duration:1.4s]"
        style={{ width: dims.icon, height: dims.icon }}
        strokeWidth={2.4}
        fill="currentColor"
        fillOpacity={0.18}
        aria-hidden="true"
      />
    </div>
  );

  if (bare) {
    return (
      <div
        className={`inline-flex items-center justify-center ${className}`}
        role="status"
        aria-live="polite"
        aria-label={label || "Loading"}
        data-testid="spinner"
      >
        {visual}
      </div>
    );
  }

  return (
    <div
      className={`w-full flex flex-col items-center justify-center gap-2 ${dims.pad} ${className}`}
      role="status"
      aria-live="polite"
      aria-label={label || "Loading"}
      data-testid="spinner"
    >
      {visual}
      {label && <span className="text-xs text-neutral-500">{label}</span>}
    </div>
  );
}
